import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';

function ApprovalTypeDialog({
  open,
  onClose,
  onSubmit,
  editData,
  approvalCategories, // [{ id, label, ... }]
  tagOptions = [], // optional dropdown options for tags
}) {
  const [formValues, setFormValues] = useState({
    categoryId: null,
    tagId: null,
    displayOrder: '',
    description: null,
    active: true,
  });

  const [errors, setErrors] = useState({});

  // Populate form when dialog opens in edit mode
  useEffect(() => {
    if (open) {
      if (editData) {
        // Editing mode
        setFormValues({
          id: editData.id,
          categoryId: editData.categoryId || null,
          tagId: editData.tagId || null,
          displayOrder: editData.displayOrder ?? 0,
          description: editData.description || null,
          active: editData.active
        });
      } else {
        // Creating mode — clear form
        setFormValues({
          categoryId: null,
          tagId: null,
          displayOrder: '',
          description: null,
          active: true,
        });
      }
      setErrors({});
    }
  }, [open, editData]);

  const handleChange = (field, val) => (e) => {
    const newVal = val !== undefined ? val : e?.target?.value
    setFormValues((prev) => ({ ...prev, [field]: newVal }));
  };

  const handleSave = () => {
    const newErrors = {};
    if (!formValues.categoryId) newErrors.categoryId = 'Category is required';
    if (formValues.displayOrder === '') newErrors.displayOrder = 'Display order is required';
    if (!formValues.description) newErrors.description = 'Description is required';
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    onSubmit(formValues, !!editData);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>
        {editData ? 'Edit Approval Type' : 'Add Approval Type'}
      </DialogTitle>
      <DialogContent>
        {/* Category Drop-down */}
        <FormControl fullWidth margin="normal">
          <InputLabel>Category</InputLabel>
          <Select
            value={formValues.categoryId}
            onChange={handleChange('categoryId')}
            error={!!errors.categoryId}
          >
            {approvalCategories.map((cat) => (
              <MenuItem value={cat.id} key={cat.id}>
                {cat.label}
              </MenuItem>
            ))}
          </Select>
          {errors.categoryId && (
            <div style={{ color: 'red', fontSize: '0.8rem' }}>
              {errors.categoryId}
            </div>
          )}
        </FormControl>

        {/* Tag Drop-down (optional) */}
        {tagOptions.length > 0 && (
          <FormControl fullWidth margin="normal">
            <InputLabel>Tag</InputLabel>
            <Select
              value={formValues.tagId || ''}
              onChange={handleChange('tagId')}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {tagOptions.map((tag) => (
                <MenuItem value={tag.id} key={tag.id}>
                  {tag.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Display Order */}
        <TextField
          type="number"
          margin="normal"
          fullWidth
          label="Display Order"
          value={formValues.displayOrder}
          onChange={handleChange('displayOrder')}
          error={!!errors.displayOrder}
          helperText={errors.displayOrder}
        />

        {/* Description */}
        <TextField
          margin="normal"
          fullWidth
          multiline
          label="Description"
          value={formValues.description}
          onChange={handleChange('description')}
          error={!!errors.description}
          helperText={errors.description}
        />

        <div style={{ marginTop: '1rem' }}>
          <label>
            <input
              type="checkbox"
              checked={formValues.active}
              onChange={(e) => {
                const val = e.target.checked
                handleChange('active', val)()
              }}
            />{' '}
            Active
          </label>
        </div>

      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          {editData ? 'Update' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ApprovalTypeDialog;